import React, { useState, useEffect } from "react";
import "@/App.css";
import { CartProvider, useCart } from "@/contexts/CartContext";
import CartDrawer from "@/components/cart/CartDrawer";
import CartButton from "@/components/cart/CartButton";
import StoreStatus from "@/components/cart/StoreStatus";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import {
  Pizza,
  MapPin,
  Phone,
  Instagram,
  Clock,
  Utensils,
  Star,
  Heart,
  Truck,
  Users,
  Menu,
  MessageCircle,
  ExternalLink,
  ChevronsDown,
  ChevronDown,
  ChefHat,
  Sparkles,
  ShoppingCart,
  Plus,
} from "lucide-react";

// Dados do Cardápio
const menuData = {
  salgadas: [
    { id: 1, name: "Calabresa", description: "Calabresa fatiada, cebola, azeitona e orégano", price: "R$ 35,00", image: "https://images.unsplash.com/photo-1565299624946-b28f40a0ae38?w=400&h=300&fit=crop", popular: true },
    { id: 2, name: "Frango com Catupiry", description: "Frango desfiado, catupiry cremoso e orégano", price: "R$ 38,00", image: "https://images.unsplash.com/photo-1604382354936-07c5d9983bd3?w=400&h=300&fit=crop", popular: true },
    { id: 3, name: "Portuguesa", description: "Presunto, ovo, cebola, ervilha, azeitona e orégano", price: "R$ 40,00", image: "https://images.unsplash.com/photo-1593504049359-74330189a345?w=400&h=300&fit=crop" },
    { id: 4, name: "Quatro Queijos", description: "Mussarela, provolone, parmesão e catupiry", price: "R$ 42,00", image: "https://images.unsplash.com/photo-1513104890138-7c749659a591?w=400&h=300&fit=crop", popular: true },
    { id: 5, name: "Margherita", description: "Mussarela, tomate fresco, manjericão e azeite", price: "R$ 36,00", image: "https://images.unsplash.com/photo-1574071318508-1cdbab80d002?w=400&h=300&fit=crop" },
    { id: 6, name: "Bacon", description: "Bacon crocante, mussarela, cebola e orégano", price: "R$ 40,00", image: "https://images.unsplash.com/photo-1628840042765-356cda07504e?w=400&h=300&fit=crop" },
  ],
  doces: [
    { id: 7, name: "Chocolate", description: "Chocolate ao leite, granulado e leite condensado", price: "R$ 35,00", image: "https://images.unsplash.com/photo-1481391319762-47dff72954d9?w=400&h=300&fit=crop", popular: true },
    { id: 8, name: "Romeu e Julieta", description: "Goiabada cremosa com queijo minas", price: "R$ 35,00", image: "https://images.unsplash.com/photo-1565299507177-b0ac66763828?w=400&h=300&fit=crop" },
    { id: 9, name: "Banana com Canela", description: "Banana, açúcar, canela e leite condensado", price: "R$ 32,00", image: "https://images.unsplash.com/photo-1571066811602-716837d681de?w=400&h=300&fit=crop" },
  ],
  porcoes: [
    { id: 10, name: "Batata Frita", description: "Porção generosa de batata frita crocante", price: "R$ 20,00", image: "https://images.unsplash.com/photo-1573080496219-bb080dd4f877?w=400&h=300&fit=crop" },
    { id: 11, name: "Frango à Passarinho", description: "Frango temperado e frito com limão", price: "R$ 28,00", image: "https://images.unsplash.com/photo-1626645738196-c2a7c87a8f58?w=400&h=300&fit=crop" },
    { id: 12, name: "Calabresa Acebolada", description: "Calabresa grelhada com cebola", price: "R$ 25,00", image: "https://images.unsplash.com/photo-1544025162-d76694265947?w=400&h=300&fit=crop" },
  ],
  bebidas: [
    { id: 13, name: "Refrigerante 2L", description: "Coca-Cola, Guaraná ou Fanta", price: "R$ 14,00", image: "https://images.unsplash.com/photo-1622483767028-3f66f32aef97?w=400&h=300&fit=crop" },
    { id: 14, name: "Refrigerante 1L", description: "Coca-Cola, Guaraná ou Fanta", price: "R$ 10,00", image: "https://images.unsplash.com/photo-1625772299848-391b6a87d7b3?w=400&h=300&fit=crop" },
    { id: 15, name: "Refrigerante 600ml", description: "Coca-Cola, Guaraná ou Fanta", price: "R$ 7,00", image: "https://images.unsplash.com/photo-1581636625402-29b2a704ef13?w=400&h=300&fit=crop" },
    { id: 16, name: "Refrigerante 350ml", description: "Lata - Diversas opções", price: "R$ 5,00", image: "https://images.unsplash.com/photo-1554866585-cd94860890b7?w=400&h=300&fit=crop" },
  ],
};

const features = [
  { icon: ChefHat, title: "Ingredientes Selecionados", description: "Utilizamos apenas ingredientes frescos e de primeira qualidade" },
  { icon: Heart, title: "Feita com Amor", description: "Cada pizza é preparada com dedicação e carinho" },
  { icon: Users, title: "Ambiente Acolhedor", description: "Espaço perfeito para família e amigos" },
  { icon: Truck, title: "Delivery Rápido", description: "Entregamos quentinha na sua casa" },
];

const WHATSAPP_NUMBER = "5588920005193";
const WHATSAPP_URL = `https://wa.me/${WHATSAPP_NUMBER}?text=Olá! Gostaria de fazer um pedido.`;
const MENU_URL = "https://pizzaria021.menudino.com/";
const INSTAGRAM_URL = "https://instagram.com/pizzaria021";

// Header Component
const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { setIsCartOpen, getTotalItems } = useCart();
  const totalItems = getTotalItems();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { href: "#inicio", label: "Início" },
    { href: "#sobre", label: "Sobre" },
    { href: "#cardapio", label: "Cardápio" },
    { href: "#diferenciais", label: "Diferenciais" },
    { href: "#localizacao", label: "Localização" },
  ];

  return (
    <header
      data-testid="header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? "bg-pizzaria-bg/95 backdrop-blur-md shadow-lg" : "bg-pizzaria-bg/95 backdrop-blur-md"
      }`}
    >
      <div className="container mx-auto px-3 sm:px-4 md:px-6">
        <div className="flex items-center justify-between h-14 sm:h-16 md:h-20">
          {/* Logo */}
          <a href="#inicio" className="flex items-center gap-1.5 sm:gap-2" data-testid="logo">
            <Pizza className="w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8 text-primary" />
            <span className="font-heading text-base sm:text-lg md:text-xl lg:text-2xl font-bold text-white uppercase tracking-wider">
              Pizzaria <span className="text-primary">021</span>
            </span>
          </a>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-6 xl:gap-8" data-testid="desktop-nav">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                className="text-pizzaria-text-secondary hover:text-white transition-colors duration-200 font-medium text-sm uppercase tracking-wide"
              >
                {link.label}
              </a>
            ))}
          </nav>

          {/* Desktop Right Section */}
          <div className="hidden md:flex items-center gap-2 lg:gap-3">
            <StoreStatus />
            
            {/* Cart Button */}
            <button
              onClick={() => setIsCartOpen(true)}
              className="relative flex items-center gap-2 px-3 lg:px-4 py-2 rounded-lg border border-white/20 hover:bg-white/10 transition-colors"
            >
              <ShoppingCart className="w-4 h-4 lg:w-5 lg:h-5 text-white" />
              <span className="text-white text-sm hidden lg:inline">Carrinho</span>
              {totalItems > 0 && (
                <span className="absolute -top-2 -right-2 w-5 h-5 bg-primary text-white rounded-full text-xs font-bold flex items-center justify-center">
                  {totalItems}
                </span>
              )}
            </button>

            <Button
              data-testid="header-cta-btn"
              asChild
              className="bg-primary hover:bg-primary/90 text-white font-heading uppercase tracking-wide btn-glow text-xs lg:text-sm px-3 lg:px-4"
            >
              <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer">
                <MessageCircle className="w-4 h-4 mr-1 lg:mr-2" />
                <span className="hidden lg:inline">Pedir Agora</span>
                <span className="lg:hidden">Pedir</span>
              </a>
            </Button>
          </div>

          {/* Mobile Right Section */}
          <div className="flex md:hidden items-center gap-2">
            <StoreStatus compact />
            
            {/* Mobile Cart Button */}
            <button
              onClick={() => setIsCartOpen(true)}
              className="relative p-2 rounded-lg border border-white/20 hover:bg-white/10 transition-colors"
            >
              <ShoppingCart className="w-5 h-5 text-white" />
              {totalItems > 0 && (
                <span className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-primary text-white rounded-full text-xs font-bold flex items-center justify-center">
                  {totalItems}
                </span>
              )}
            </button>

            {/* Mobile Menu */}
            <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" data-testid="mobile-menu-btn" className="h-9 w-9">
                  <Menu className="w-5 h-5 text-white" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="bg-pizzaria-bg border-white/10 w-[280px] sm:w-[320px]">
                <div className="flex flex-col gap-4 sm:gap-6 mt-6 sm:mt-8">
                  {navLinks.map((link) => (
                    <a
                      key={link.href}
                      href={link.href}
                      onClick={() => setMobileMenuOpen(false)}
                      className="text-pizzaria-text hover:text-primary transition-colors duration-200 font-heading text-base sm:text-lg uppercase tracking-wide"
                    >
                      {link.label}
                    </a>
                  ))}
                  <Separator className="bg-white/10" />
                  <StoreStatus />
                  <Separator className="bg-white/10" />
                  <Button
                    data-testid="mobile-cta-btn"
                    asChild
                    className="bg-primary hover:bg-primary/90 text-white font-heading uppercase"
                  >
                    <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer">
                      <MessageCircle className="w-4 h-4 mr-2" />
                      Pedir Agora
                    </a>
                  </Button>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </header>
  );
};

// Hero Section
const HeroSection = () => {
  const { storeStatus } = useCart();

  return (
    <section
      id="inicio"
      data-testid="hero-section"
      className="relative min-h-[100svh] flex items-center justify-center overflow-hidden"
    >
      {/* Background Image */}
      <div className="absolute inset-0 z-0">
        <img
          src="https://images.unsplash.com/photo-1565299624946-b28f40a0ae38?w=1920&h=1080&fit=crop"
          alt="Pizza deliciosa"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 hero-overlay" />
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 sm:px-6 relative z-10 text-center pt-16 sm:pt-20 pb-8">
        <div className="max-w-4xl mx-auto">
          {/* Location & Status Badge */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-4 mb-4 sm:mb-6 animate-fade-in">
            <div className="inline-flex items-center gap-2 bg-black/55 border border-white/30 backdrop-blur-md px-3 sm:px-4 py-1.5 sm:py-2 rounded-full shadow-lg">
              <MapPin className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-primary" />
              <span className="text-xs sm:text-sm text-white font-semibold tracking-wide">Carnaubal - CE</span>
            </div>
            <div className={`inline-flex items-center gap-2 border backdrop-blur-md px-3 sm:px-4 py-1.5 sm:py-2 rounded-full shadow-lg ${
              storeStatus.isOpen 
                ? 'bg-emerald-900/55 text-emerald-100 border-emerald-300/60' 
                : 'bg-red-900/60 text-red-100 border-red-300/70'
            }`}>
              <div className={`w-2 h-2 rounded-full animate-pulse ${
                storeStatus.isOpen ? 'bg-emerald-300' : 'bg-red-300'
              }`} />
              <span className="text-xs sm:text-sm font-semibold tracking-wide">
                {storeStatus.isOpen ? 'Aberto agora' : 'Fechado'}
              </span>
            </div>
          </div>

          {/* Main Title */}
          <h1
            data-testid="hero-title"
            className="font-heading text-4xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl font-bold text-white uppercase tracking-tight mb-4 sm:mb-6 animate-fade-in-up"
          >
            Pizzaria{" "}
            <span className="text-primary">021</span>
          </h1>

          {/* Subtitle */}
          <p
            data-testid="hero-subtitle"
            className="text-base sm:text-lg md:text-xl lg:text-2xl text-pizzaria-text-secondary mb-6 sm:mb-8 max-w-2xl mx-auto px-2 animate-fade-in-up animate-delay-200"
          >
            O verdadeiro sabor da pizza artesanal. Feita com ingredientes selecionados e muito amor.
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4 animate-fade-in-up animate-delay-300 px-4 sm:px-0">
            <Button
              data-testid="hero-order-btn"
              asChild
              size="lg"
              className="w-full sm:w-auto bg-primary hover:bg-primary/90 text-white font-heading uppercase tracking-wide text-sm sm:text-base lg:text-lg px-6 sm:px-8 py-5 sm:py-6 btn-glow"
            >
              <a href="#cardapio">
                <ShoppingCart className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                Ver Cardápio
              </a>
            </Button>
            <Button
              data-testid="hero-menu-btn"
              asChild
              size="lg"
              variant="outline"
              className="w-full sm:w-auto border-white/30 text-white hover:bg-white/10 font-heading uppercase tracking-wide text-sm sm:text-base lg:text-lg px-6 sm:px-8 py-5 sm:py-6"
            >
              <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer">
                <MessageCircle className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                WhatsApp
              </a>
            </Button>
          </div>

          {/* Schedule Info */}
          <div className="flex items-center justify-center gap-2 mt-6 sm:mt-8 text-pizzaria-text-secondary animate-fade-in-up animate-delay-400">
            <Clock className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
            <span className="text-xs sm:text-sm">Dom a Sáb • 19h às 23h45 (exceto Terça)</span>
          </div>
        </div>
      </div>

      {/* Scroll Indicator - Mobile */}
      <div className="absolute bottom-5 left-1/2 -translate-x-1/2 sm:hidden flex flex-col items-center z-20">
        <span className="text-[10px] uppercase tracking-[0.2em] text-white/85 font-semibold">
          Desça a página
        </span>
        <ChevronsDown className="w-6 h-6 text-white/90 mt-1 animate-bounce" />
      </div>

      {/* Scroll Indicator - Hidden on mobile */}
      <div className="absolute bottom-6 sm:bottom-8 left-1/2 -translate-x-1/2 animate-bounce hidden sm:block">
        <div className="w-5 h-8 sm:w-6 sm:h-10 border-2 border-white/30 rounded-full flex justify-center">
          <div className="w-1 h-2 sm:h-3 bg-white/50 rounded-full mt-1.5 sm:mt-2" />
        </div>
      </div>
    </section>
  );
};

// About Section
const AboutSection = () => {
  return (
    <section id="sobre" data-testid="about-section" className="py-12 sm:py-16 md:py-20 lg:py-28 bg-pizzaria-bg">
      <div className="container mx-auto px-4 sm:px-6">
        <div className="grid lg:grid-cols-2 gap-8 sm:gap-10 lg:gap-16 items-center">
          {/* Image */}
          <div className="relative order-2 lg:order-1">
            <div className="relative rounded-xl sm:rounded-2xl overflow-hidden shadow-2xl">
              <img
                src="https://images.unsplash.com/photo-1625021658521-bec8b1ba0b6f?w=600&h=500&fit=crop"
                alt="Chef preparando pizza"
                className="w-full h-[280px] sm:h-[350px] md:h-[400px] lg:h-[500px] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
            </div>
            {/* Floating Badge */}
            <div className="absolute -bottom-4 right-4 sm:-bottom-6 sm:right-6 md:bottom-6 md:right-6 lg:bottom-8 lg:right-8 bg-primary text-white p-3 sm:p-4 md:p-6 rounded-xl sm:rounded-2xl shadow-xl">
              <div className="text-center">
                <span className="font-heading text-2xl sm:text-3xl md:text-4xl font-bold">+5</span>
                <p className="text-[10px] sm:text-xs md:text-sm uppercase tracking-wide">Anos de tradição</p>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="order-1 lg:order-2">
            <Badge className="bg-primary/20 text-primary border-primary/30 mb-3 sm:mb-4 text-xs">
              <Star className="w-3 h-3 mr-1" />
              Sobre Nós
            </Badge>
            <h2
              data-testid="about-title"
              className="font-heading text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-white uppercase tracking-tight mb-4 sm:mb-6"
            >
              Mais que uma pizzaria,{" "}
              <span className="text-primary">uma experiência</span>
            </h2>
            <div className="space-y-3 sm:space-y-4 text-pizzaria-text-secondary text-sm sm:text-base md:text-lg leading-relaxed">
              <p>
                A <strong className="text-white">Pizzaria 021</strong> nasceu do sonho de levar o verdadeiro sabor da pizza artesanal para Carnaubal e região. Com ingredientes selecionados e receitas especiais, cada pizza é preparada com dedicação e amor.
              </p>
              <p>
                Nosso ambiente foi pensado para você se sentir em casa. Seja para um jantar em família, encontro com amigos ou aquele momento especial a dois, aqui você encontra o lugar perfeito.
              </p>
              <p className="hidden sm:block">
                Venha viver essa experiência gastronômica única. Estamos esperando você!
              </p>
            </div>
            <div className="flex flex-col xs:flex-row gap-3 sm:gap-4 mt-6 sm:mt-8">
              <Button
                data-testid="about-visit-btn"
                asChild
                className="bg-primary hover:bg-primary/90 text-white font-heading uppercase text-sm"
              >
                <a href="#localizacao">
                  <MapPin className="w-4 h-4 mr-2" />
                  Nos Visite
                </a>
              </Button>
              <Button
                data-testid="about-whatsapp-btn"
                asChild
                variant="outline"
                className="border-white/30 text-white hover:bg-white/10 font-heading uppercase text-sm"
              >
                <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer">
                  <Phone className="w-4 h-4 mr-2" />
                  Fale Conosco
                </a>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Menu Item Card with Add to Cart
const MenuItemCard = ({ item }) => {
  const { addItem } = useCart();
  const [added, setAdded] = useState(false);

  const handleAddToCart = () => {
    addItem(item);
    setAdded(true);
    setTimeout(() => setAdded(false), 1500);
  };

  return (
    <Card
      data-testid={`menu-item-${item.id}`}
      className="pizza-card bg-pizzaria-paper border-white/10 overflow-hidden card-hover group"
    >
      <div className="relative h-36 sm:h-40 md:h-44 lg:h-48 overflow-hidden">
        <img
          src={item.image}
          alt={item.name}
          className="pizza-card-img w-full h-full object-cover"
        />
        {item.popular && (
          <Badge className="menu-badge absolute top-2 right-2 sm:top-3 sm:right-3 text-white border-0 text-[10px] sm:text-xs px-1.5 sm:px-2 py-0.5">
            <Sparkles className="w-2.5 h-2.5 sm:w-3 sm:h-3 mr-0.5 sm:mr-1" />
            Popular
          </Badge>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
      </div>
      <CardContent className="p-3 sm:p-4">
        <div className="flex justify-between items-start gap-2 mb-1.5 sm:mb-2">
          <h3 className="font-heading text-sm sm:text-base lg:text-lg font-bold text-white uppercase leading-tight">
            {item.name}
          </h3>
          <span className="font-heading text-sm sm:text-base lg:text-lg font-bold text-primary whitespace-nowrap">
            {item.price}
          </span>
        </div>
        <p className="text-pizzaria-text-secondary text-xs sm:text-sm mb-3 sm:mb-4 line-clamp-2 leading-relaxed">
          {item.description}
        </p>
        <Button
          data-testid={`order-item-${item.id}`}
          onClick={handleAddToCart}
          size="sm"
          className={`w-full font-heading uppercase text-[10px] sm:text-xs transition-all duration-300 ${
            added 
              ? 'bg-green-600 hover:bg-green-600 text-white' 
              : 'bg-primary/20 hover:bg-primary text-primary hover:text-white'
          }`}
        >
          {added ? (
            <>
              <span className="mr-1">✓</span>
              Adicionado!
            </>
          ) : (
            <>
              <Plus className="w-3 h-3 sm:w-3.5 sm:h-3.5 mr-1" />
              Adicionar
            </>
          )}
        </Button>
      </CardContent>
    </Card>
  );
};

// Menu Section
const MenuSection = () => {
  const { storeStatus, setIsCartOpen, getTotalItems, getTotal } = useCart();
  const totalItems = getTotalItems();
  const total = getTotal();

  return (
    <section id="cardapio" data-testid="menu-section" className="py-12 sm:py-16 md:py-20 lg:py-28 bg-pizzaria-paper">
      <div className="container mx-auto px-3 sm:px-4 md:px-6">
        {/* Section Header */}
        <div className="text-center mb-8 sm:mb-10 md:mb-12">
          <Badge className="bg-primary/20 text-primary border-primary/30 mb-3 sm:mb-4 text-xs">
            <Utensils className="w-3 h-3 mr-1" />
            Cardápio
          </Badge>
          <h2
            data-testid="menu-title"
            className="font-heading text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-white uppercase tracking-tight mb-3 sm:mb-4"
          >
            Nosso <span className="text-primary">Cardápio</span>
          </h2>
          <p className="text-pizzaria-text-secondary text-sm sm:text-base md:text-lg max-w-2xl mx-auto px-2">
            Descubra nossos sabores irresistíveis. Cada pizza é feita com ingredientes frescos e muito carinho.
          </p>
          
          {/* Store Status Alert */}
          {!storeStatus.isOpen && (
            <div className="mt-4 sm:mt-6 inline-flex items-center gap-2 bg-yellow-500/10 border border-yellow-500/30 px-4 py-2 rounded-lg">
              <Clock className="w-4 h-4 text-yellow-500" />
              <span className="text-yellow-500 text-xs sm:text-sm">
                {storeStatus.message}
              </span>
            </div>
          )}
        </div>

        {/* Tabs */}
        <Tabs defaultValue="salgadas" className="w-full">
          <TabsList
            data-testid="menu-tabs"
            className="w-full grid grid-cols-2 sm:grid-cols-4 gap-2 bg-transparent mb-6 sm:mb-8 h-auto p-0"
          >
            <TabsTrigger
              value="salgadas"
              data-testid="tab-salgadas"
              className="tab-underline w-full min-h-10 bg-pizzaria-subtle data-[state=active]:bg-primary data-[state=active]:text-white font-heading uppercase tracking-wide px-2 sm:px-3 md:px-4 py-2 text-[11px] sm:text-sm"
            >
              <Pizza className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
              Salgadas
            </TabsTrigger>
            <TabsTrigger
              value="doces"
              data-testid="tab-doces"
              className="tab-underline w-full min-h-10 bg-pizzaria-subtle data-[state=active]:bg-primary data-[state=active]:text-white font-heading uppercase tracking-wide px-2 sm:px-3 md:px-4 py-2 text-[11px] sm:text-sm"
            >
              <Heart className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
              Doces
            </TabsTrigger>
            <TabsTrigger
              value="porcoes"
              data-testid="tab-porcoes"
              className="tab-underline w-full min-h-10 bg-pizzaria-subtle data-[state=active]:bg-primary data-[state=active]:text-white font-heading uppercase tracking-wide px-2 sm:px-3 md:px-4 py-2 text-[11px] sm:text-sm"
            >
              <Utensils className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
              Porções
            </TabsTrigger>
            <TabsTrigger
              value="bebidas"
              data-testid="tab-bebidas"
              className="tab-underline w-full min-h-10 bg-pizzaria-subtle data-[state=active]:bg-primary data-[state=active]:text-white font-heading uppercase tracking-wide px-2 sm:px-3 md:px-4 py-2 text-[11px] sm:text-sm"
            >
              <Star className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
              Bebidas
            </TabsTrigger>
          </TabsList>

          <TabsContent value="salgadas" className="mt-0">
            <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4 md:gap-5 lg:gap-6">
              {menuData.salgadas.map((item) => (
                <MenuItemCard key={item.id} item={item} />
              ))}
            </div>
          </TabsContent>

          <TabsContent value="doces" className="mt-0">
            <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4 md:gap-5 lg:gap-6">
              {menuData.doces.map((item) => (
                <MenuItemCard key={item.id} item={item} />
              ))}
            </div>
          </TabsContent>

          <TabsContent value="porcoes" className="mt-0">
            <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4 md:gap-5 lg:gap-6">
              {menuData.porcoes.map((item) => (
                <MenuItemCard key={item.id} item={item} />
              ))}
            </div>
          </TabsContent>

          <TabsContent value="bebidas" className="mt-0">
            <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4 md:gap-5 lg:gap-6">
              {menuData.bebidas.map((item) => (
                <MenuItemCard key={item.id} item={item} />
              ))}
            </div>
          </TabsContent>
        </Tabs>

        {/* Cart Summary Bar - Shows when items in cart */}
        {totalItems > 0 && (
          <div className="fixed bottom-0 left-0 right-0 bg-pizzaria-bg/95 backdrop-blur-md border-t border-white/10 p-3 sm:p-4 z-40 md:hidden">
            <div className="container mx-auto">
              <Button
                onClick={() => setIsCartOpen(true)}
                className="w-full bg-primary hover:bg-primary/90 text-white font-heading uppercase py-5 sm:py-6"
              >
                <ShoppingCart className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                Ver Carrinho ({totalItems}) • R$ {total.toFixed(2).replace('.', ',')}
              </Button>
            </div>
          </div>
        )}

        {/* View Full Menu Button */}
        <div className="text-center mt-8 sm:mt-10 md:mt-12">
          <Button
            data-testid="view-full-menu-btn"
            asChild
            size="lg"
            variant="outline"
            className="border-white/30 text-white hover:bg-white/10 font-heading uppercase tracking-wide text-sm sm:text-base"
          >
            <a href={MENU_URL} target="_blank" rel="noopener noreferrer">
              <ExternalLink className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
              Ver Cardápio Completo no Menudino
            </a>
          </Button>
        </div>
      </div>
    </section>
  );
};

// Features Section
const FeaturesSection = () => {
  return (
    <section id="diferenciais" data-testid="features-section" className="py-12 sm:py-16 md:py-20 lg:py-28 bg-pizzaria-bg">
      <div className="container mx-auto px-4 sm:px-6">
        {/* Section Header */}
        <div className="text-center mb-8 sm:mb-10 md:mb-12">
          <Badge className="bg-primary/20 text-primary border-primary/30 mb-3 sm:mb-4 text-xs">
            <Star className="w-3 h-3 mr-1" />
            Diferenciais
          </Badge>
          <h2
            data-testid="features-title"
            className="font-heading text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-white uppercase tracking-tight mb-3 sm:mb-4"
          >
            Por que escolher a{" "}
            <span className="text-primary">021</span>?
          </h2>
          <p className="text-pizzaria-text-secondary text-sm sm:text-base md:text-lg max-w-2xl mx-auto">
            Compromisso com qualidade, sabor e experiência em cada detalhe.
          </p>
        </div>

        {/* Features Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 md:gap-6">
          {features.map((feature, index) => (
            <Card
              key={index}
              data-testid={`feature-${index}`}
              className="bg-pizzaria-paper border-white/10 text-center p-4 sm:p-5 md:p-6 card-hover group"
            >
              <div className="w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 mx-auto mb-3 sm:mb-4 rounded-full bg-primary/20 flex items-center justify-center group-hover:bg-primary transition-colors duration-300">
                <feature.icon className="w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8 text-primary group-hover:text-white transition-colors duration-300" />
              </div>
              <h3 className="font-heading text-xs sm:text-sm md:text-base lg:text-lg font-bold text-white uppercase mb-1.5 sm:mb-2 leading-tight">
                {feature.title}
              </h3>
              <p className="text-pizzaria-text-secondary text-[10px] sm:text-xs md:text-sm leading-relaxed">
                {feature.description}
              </p>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
};

// Promotions Section
const PromotionsSection = () => {
  return (
    <section id="promocoes" data-testid="promotions-section" className="py-12 sm:py-16 md:py-20 lg:py-28 bg-pizzaria-paper">
      <div className="container mx-auto px-4 sm:px-6">
        {/* Promo Banner */}
        <div
          data-testid="promo-banner"
          className="promo-shine relative rounded-2xl sm:rounded-3xl overflow-hidden bg-gradient-to-r from-primary to-secondary p-5 sm:p-8 md:p-12 lg:p-16"
        >
          <div className="relative z-10 max-w-3xl">
            <Badge className="bg-white/20 text-white border-white/30 mb-3 sm:mb-4 text-xs">
              <Sparkles className="w-3 h-3 mr-1" />
              Ofertas Especiais
            </Badge>
            <h2 className="font-heading text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-white uppercase tracking-tight mb-3 sm:mb-4">
              Promoções da Semana
            </h2>
            <p className="text-white/90 text-sm sm:text-base md:text-lg mb-5 sm:mb-6 max-w-xl">
              Fique de olho em nossas ofertas especiais! Combos, descontos e muito mais. Siga-nos no Instagram para não perder nenhuma novidade.
            </p>
            <div className="flex flex-col xs:flex-row gap-3 sm:gap-4">
              <Button
                data-testid="promo-order-btn"
                asChild
                size="lg"
                className="bg-white hover:bg-white/90 text-primary font-heading uppercase tracking-wide text-sm sm:text-base"
              >
                <a href="#cardapio">
                  <ShoppingCart className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                  Ver Cardápio
                </a>
              </Button>
              <Button
                data-testid="promo-instagram-btn"
                asChild
                size="lg"
                variant="outline"
                className="border-white/50 text-white hover:bg-white/10 font-heading uppercase tracking-wide text-sm sm:text-base"
              >
                <a href={INSTAGRAM_URL} target="_blank" rel="noopener noreferrer">
                  <Instagram className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                  Instagram
                </a>
              </Button>
            </div>
          </div>

          {/* Decorative Elements */}
          <div className="absolute right-0 top-0 w-1/3 h-full opacity-10 sm:opacity-20">
            <Pizza className="w-full h-full text-white" />
          </div>
        </div>
      </div>
    </section>
  );
};

// Location Section
const LocationSection = () => {
  const { getScheduleInfo, storeStatus } = useCart();
  const schedule = getScheduleInfo();
  const [isScheduleMinimized, setIsScheduleMinimized] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth < 640 : false
  );

  return (
    <section id="localizacao" data-testid="location-section" className="py-12 sm:py-16 md:py-20 lg:py-28 bg-pizzaria-bg">
      <div className="container mx-auto px-4 sm:px-6">
        {/* Section Header */}
        <div className="text-center mb-8 sm:mb-10 md:mb-12">
          <Badge className="bg-primary/20 text-primary border-primary/30 mb-3 sm:mb-4 text-xs">
            <MapPin className="w-3 h-3 mr-1" />
            Localização
          </Badge>
          <h2
            data-testid="location-title"
            className="font-heading text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-white uppercase tracking-tight mb-3 sm:mb-4"
          >
            Onde <span className="text-primary">Estamos</span>
          </h2>
          <p className="text-pizzaria-text-secondary text-sm sm:text-base md:text-lg max-w-2xl mx-auto">
            Venha nos visitar! Estamos localizados no coração de Carnaubal.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-6 sm:gap-8 lg:gap-12">
          {/* Map */}
          <div className="map-container rounded-xl sm:rounded-2xl overflow-hidden shadow-2xl h-[250px] sm:h-[300px] md:h-[350px] lg:h-[400px]">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3977.5!2d-40.9422!3d-4.1589!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zNMKwMDknMzIuMCJTIDQwwrA1NicyMi44Ilc!5e0!3m2!1spt-BR!2sbr!4v1234567890"
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Localização Pizzaria 021"
            />
          </div>

          <div className="hidden lg:block lg:col-start-1 lg:row-start-2 space-y-4 sm:space-y-5 md:space-y-6">
            <Card className="bg-pizzaria-paper border-white/10 p-4 sm:p-5 md:p-6">
              <div className="flex items-start gap-3 sm:gap-4">
                <div className="w-10 h-10 sm:w-11 sm:h-11 md:w-12 md:h-12 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
                  <Phone className="w-5 h-5 sm:w-5 sm:h-5 md:w-6 md:h-6 text-primary" />
                </div>
                <div className="flex-1">
                  <h3 className="font-heading text-sm sm:text-base md:text-lg font-bold text-white uppercase mb-1">Contato</h3>
                  <p className="text-pizzaria-text-secondary text-sm sm:text-base mb-3">
                    (88) 92000-5193
                  </p>
                  <Button
                    asChild
                    className="bg-green-600 hover:bg-green-700 text-white font-heading uppercase text-xs sm:text-sm w-full sm:w-auto"
                  >
                    <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer">
                      <MessageCircle className="w-4 h-4 mr-2" />
                      Falar no WhatsApp
                    </a>
                  </Button>
                </div>
              </div>
            </Card>

            <Button
              asChild
              size="lg"
              variant="outline"
              className="w-full border-white/30 text-white hover:bg-white/10 font-heading uppercase text-sm"
            >
              <a
                href="https://www.google.com/maps/search/Pizzaria+021+Carnaubal+CE"
                target="_blank"
                rel="noopener noreferrer"
              >
                <ExternalLink className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                Abrir no Google Maps
              </a>
            </Button>
          </div>

          {/* Contact Info */}
          <div className="space-y-4 sm:space-y-5 md:space-y-6 lg:col-start-2 lg:row-start-1 lg:row-span-2">
            {/* Address Card */}
            <Card className="bg-pizzaria-paper border-white/10 p-4 sm:p-5 md:p-6">
              <div className="flex items-start gap-3 sm:gap-4">
                <div className="w-10 h-10 sm:w-11 sm:h-11 md:w-12 md:h-12 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-5 h-5 sm:w-5 sm:h-5 md:w-6 md:h-6 text-primary" />
                </div>
                <div>
                  <h3 className="font-heading text-sm sm:text-base md:text-lg font-bold text-white uppercase mb-1">Endereço</h3>
                  <p className="text-pizzaria-text-secondary text-sm sm:text-base">
                    Av. Paulo Sarasate, 207 - Centro<br />
                    Carnaubal - CE
                  </p>
                </div>
              </div>
            </Card>

            {/* Schedule Card */}
            <Card className="bg-pizzaria-paper border-white/10 p-4 sm:p-5 md:p-6">
              <div className="flex items-start gap-3 sm:gap-4">
                <div className="w-10 h-10 sm:w-11 sm:h-11 md:w-12 md:h-12 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
                  <Clock className="w-5 h-5 sm:w-5 sm:h-5 md:w-6 md:h-6 text-primary" />
                </div>
                <div className="flex-1">
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <h3 className="font-heading text-sm sm:text-base md:text-lg font-bold text-white uppercase">Horários</h3>
                    <div className="flex items-center gap-2">
                      <div className={`px-2 py-0.5 rounded text-xs font-medium ${
                        storeStatus.isOpen 
                          ? 'bg-green-500/20 text-green-400' 
                          : 'bg-red-500/20 text-red-400'
                      }`}>
                        {storeStatus.isOpen ? 'Aberto' : 'Fechado'}
                      </div>
                      <button
                        type="button"
                        onClick={() => setIsScheduleMinimized((prev) => !prev)}
                        aria-expanded={!isScheduleMinimized}
                        className="inline-flex items-center gap-1 text-[11px] text-pizzaria-text-secondary hover:text-white transition-colors"
                      >
                        {isScheduleMinimized ? 'Expandir' : 'Minimizar'}
                        <ChevronDown className={`w-3.5 h-3.5 transition-transform ${isScheduleMinimized ? '' : 'rotate-180'}`} />
                      </button>
                    </div>
                  </div>
                  <div className={`${isScheduleMinimized ? 'hidden' : 'grid'} grid-cols-2 gap-2 text-xs sm:text-sm`}>
                    {schedule.map((day, index) => (
                      <div 
                        key={index} 
                        className={`rounded-lg border border-white/10 px-2 py-2 flex flex-col gap-1 ${
                          day.isOpen ? 'text-pizzaria-text-secondary' : 'text-pizzaria-text-muted'
                        }`}
                      >
                        <span className="font-semibold text-[11px] uppercase tracking-wide">{day.day}</span>
                        <span className={`leading-tight ${!day.isOpen ? 'text-red-400/70' : ''}`}>
                          {day.hours}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </Card>

            {/* Contact Card */}
            <Card className="lg:hidden bg-pizzaria-paper border-white/10 p-4 sm:p-5 md:p-6">
              <div className="flex items-start gap-3 sm:gap-4">
                <div className="w-10 h-10 sm:w-11 sm:h-11 md:w-12 md:h-12 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
                  <Phone className="w-5 h-5 sm:w-5 sm:h-5 md:w-6 md:h-6 text-primary" />
                </div>
                <div className="flex-1">
                  <h3 className="font-heading text-sm sm:text-base md:text-lg font-bold text-white uppercase mb-1">Contato</h3>
                  <p className="text-pizzaria-text-secondary text-sm sm:text-base mb-3">
                    (88) 92000-5193
                  </p>
                  <Button
                    data-testid="location-whatsapp-btn"
                    asChild
                    className="bg-green-600 hover:bg-green-700 text-white font-heading uppercase text-xs sm:text-sm w-full sm:w-auto"
                  >
                    <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer">
                      <MessageCircle className="w-4 h-4 mr-2" />
                      Falar no WhatsApp
                    </a>
                  </Button>
                </div>
              </div>
            </Card>

            <Button
              data-testid="open-maps-btn"
              asChild
              size="lg"
              variant="outline"
              className="lg:hidden w-full border-white/30 text-white hover:bg-white/10 font-heading uppercase text-sm"
            >
              <a
                href="https://www.google.com/maps/search/Pizzaria+021+Carnaubal+CE"
                target="_blank"
                rel="noopener noreferrer"
              >
                <ExternalLink className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                Abrir no Google Maps
              </a>
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};

// Footer
const Footer = () => {
  const { getScheduleInfo } = useCart();
  const schedule = getScheduleInfo();
  const [isFooterLinksMinimized, setIsFooterLinksMinimized] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth < 640 : false
  );
  const [isFooterScheduleMinimized, setIsFooterScheduleMinimized] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth < 640 : false
  );
  const [isFooterContactMinimized, setIsFooterContactMinimized] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth < 640 : false
  );

  return (
    <footer data-testid="footer" className="bg-pizzaria-paper border-t border-white/10">
      <div className="container mx-auto px-4 sm:px-6 py-8 sm:py-10 md:py-12">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-8 text-center sm:text-left">
          {/* Brand */}
          <div className="sm:col-span-2 lg:col-span-1">
            <a href="#inicio" className="flex items-center justify-center sm:justify-start gap-2 mb-3 sm:mb-4">
              <Pizza className="w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8 text-primary" />
              <span className="font-heading text-lg sm:text-xl md:text-2xl font-bold text-white uppercase tracking-wider">
                Pizzaria <span className="text-primary">021</span>
              </span>
            </a>
            <p className="text-pizzaria-text-secondary text-sm sm:text-base mb-4 max-w-md mx-auto sm:mx-0">
              O verdadeiro sabor da pizza artesanal em Carnaubal.
            </p>
            <div className="flex justify-center sm:justify-start gap-3 sm:gap-4">
              <a
                href={INSTAGRAM_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-primary transition-colors duration-300"
                data-testid="footer-instagram"
              >
                <Instagram className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
              </a>
              <a
                href={WHATSAPP_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-green-600 transition-colors duration-300"
                data-testid="footer-whatsapp"
              >
                <MessageCircle className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div className="text-center sm:text-left">
            <h3 className="font-heading text-sm sm:text-base md:text-lg font-bold text-white uppercase mb-3 sm:mb-4">Links Rápidos</h3>
            <button
              type="button"
              onClick={() => setIsFooterLinksMinimized((prev) => !prev)}
              aria-expanded={!isFooterLinksMinimized}
              className="inline-flex items-center gap-1 text-[11px] text-pizzaria-text-secondary hover:text-white transition-colors mb-2"
            >
              {isFooterLinksMinimized ? 'Expandir' : 'Minimizar'}
              <ChevronDown className={`w-3.5 h-3.5 transition-transform ${isFooterLinksMinimized ? '' : 'rotate-180'}`} />
            </button>
            <ul className={`${isFooterLinksMinimized ? 'hidden' : 'space-y-1.5 sm:space-y-2'} text-sm sm:text-base`}>
              <li><a href="#inicio" className="text-pizzaria-text-secondary hover:text-primary transition-colors text-sm sm:text-base">Início</a></li>
              <li><a href="#sobre" className="text-pizzaria-text-secondary hover:text-primary transition-colors text-sm sm:text-base">Sobre</a></li>
              <li><a href="#cardapio" className="text-pizzaria-text-secondary hover:text-primary transition-colors text-sm sm:text-base">Cardápio</a></li>
              <li><a href="#localizacao" className="text-pizzaria-text-secondary hover:text-primary transition-colors text-sm sm:text-base">Localização</a></li>
            </ul>
          </div>

          {/* Schedule */}
          <div>
            <h3 className="font-heading text-sm sm:text-base md:text-lg font-bold text-white uppercase mb-3 sm:mb-4">Horários</h3>
            <button
              type="button"
              onClick={() => setIsFooterScheduleMinimized((prev) => !prev)}
              aria-expanded={!isFooterScheduleMinimized}
              className="inline-flex items-center gap-1 text-[11px] text-pizzaria-text-secondary hover:text-white transition-colors mb-2"
            >
              {isFooterScheduleMinimized ? 'Expandir' : 'Minimizar'}
              <ChevronDown className={`w-3.5 h-3.5 transition-transform ${isFooterScheduleMinimized ? '' : 'rotate-180'}`} />
            </button>
            <ul className={`${isFooterScheduleMinimized ? 'hidden' : 'grid'} grid-cols-2 sm:grid-cols-1 gap-2 text-xs sm:text-sm`}>
              {schedule.map((day, index) => (
                <li 
                  key={index} 
                  className={`rounded-lg border border-white/10 px-2 py-2 flex flex-col gap-1 ${
                    day.isOpen ? 'text-pizzaria-text-secondary' : 'text-pizzaria-text-muted'
                  }`}
                >
                  <span className="font-semibold text-[11px] uppercase tracking-wide">{day.day}</span>
                  <span className={`font-medium leading-tight ${!day.isOpen ? 'text-red-400/80' : ''}`}>
                    {day.hours}
                  </span>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div className="text-center sm:text-left">
            <h3 className="font-heading text-sm sm:text-base md:text-lg font-bold text-white uppercase mb-3 sm:mb-4">Contato</h3>
            <button
              type="button"
              onClick={() => setIsFooterContactMinimized((prev) => !prev)}
              aria-expanded={!isFooterContactMinimized}
              className="inline-flex items-center gap-1 text-[11px] text-pizzaria-text-secondary hover:text-white transition-colors mb-2"
            >
              {isFooterContactMinimized ? 'Expandir' : 'Minimizar'}
              <ChevronDown className={`w-3.5 h-3.5 transition-transform ${isFooterContactMinimized ? '' : 'rotate-180'}`} />
            </button>
            <ul className={`${isFooterContactMinimized ? 'hidden' : 'space-y-2 sm:space-y-3'} text-pizzaria-text-secondary text-sm sm:text-base`}>
              <li className="flex items-start justify-center sm:justify-start gap-2">
                <MapPin className="w-4 h-4 mt-0.5 text-primary flex-shrink-0" />
                <span>Av. Paulo Sarasate, 207<br />Centro, Carnaubal - CE</span>
              </li>
              <li className="flex items-center justify-center sm:justify-start gap-2">
                <Phone className="w-4 h-4 text-primary flex-shrink-0" />
                <span>(88) 92000-5193</span>
              </li>
            </ul>
          </div>
        </div>

        <Separator className="bg-white/10 my-6 sm:my-8" />

        {/* Copyright */}
        <div className="text-center text-pizzaria-text-muted text-xs sm:text-sm">
          <p>&copy; {new Date().getFullYear()} Pizzaria 021. Todos os direitos reservados.</p>
        </div>
      </div>
    </footer>
  );
};

// Floating WhatsApp Button
const FloatingWhatsApp = () => {
  const { getTotalItems } = useCart();
  const totalItems = getTotalItems();

  return (
    <a
      href={WHATSAPP_URL}
      target="_blank"
      rel="noopener noreferrer"
      data-testid="floating-whatsapp"
      className={`whatsapp-float fixed z-50 w-12 h-12 sm:w-14 sm:h-14 bg-green-500 hover:bg-green-600 rounded-full flex items-center justify-center shadow-lg transition-all duration-300 hover:scale-110 ${
        totalItems > 0 ? 'bottom-20 sm:bottom-6 right-4 sm:right-6' : 'bottom-4 sm:bottom-6 right-4 sm:right-6'
      }`}
      aria-label="Falar no WhatsApp"
    >
      <MessageCircle className="w-6 h-6 sm:w-7 sm:h-7 text-white" />
    </a>
  );
};

// Floating Cart Button (Desktop only - mobile uses bottom bar)
const FloatingCart = () => {
  const { setIsCartOpen, getTotalItems } = useCart();
  const totalItems = getTotalItems();

  if (totalItems === 0) return null;

  return (
    <button
      onClick={() => setIsCartOpen(true)}
      className="hidden md:flex fixed bottom-6 right-24 z-50 w-14 h-14 bg-primary hover:bg-primary/90 rounded-full items-center justify-center shadow-lg transition-all duration-300 hover:scale-110 animate-pulse-glow"
      aria-label="Abrir carrinho"
    >
      <ShoppingCart className="w-6 h-6 text-white" />
      <span className="absolute -top-1 -right-1 w-6 h-6 bg-white text-primary rounded-full text-xs font-bold flex items-center justify-center">
        {totalItems}
      </span>
    </button>
  );
};

// Main App Content (needs to be inside CartProvider)
function AppContent() {
  const { getTotalItems } = useCart();
  const totalItems = getTotalItems();

  return (
    <div className="App min-h-screen bg-pizzaria-bg">
      <Header />
      <main className={totalItems > 0 ? 'pb-20 md:pb-0' : ''}>
        <HeroSection />
        <div className="section-divider" />
        <AboutSection />
        <div className="section-divider" />
        <MenuSection />
        <div className="section-divider" />
        <FeaturesSection />
        <div className="section-divider" />
        <PromotionsSection />
        <div className="section-divider" />
        <LocationSection />
      </main>
      <Footer />
      <FloatingWhatsApp />
      <FloatingCart />
      <CartDrawer />
    </div>
  );
}

// Main App with Provider
function App() {
  return (
    <CartProvider>
      <AppContent />
    </CartProvider>
  );
}

export default App;
